import { http } from '@/http/http';
import { Endpoints } from '@/http/endpoints';

/** localStorage key the RUM interceptor reads for direct-navigation client
 *  resolution (legacy `LOCAL_STORAGE.TEMP_CLIENTID`). */
const TEMP_CLIENT_ID_KEY = 'tempClientId';

/**
 * Pin `clientId` in `localStorage.tempClientId` while `fn` runs so `http()`
 * mints the RUM token for this client on the clientless role-screen-mapping
 * call. Mirrors legacy `BaseRouteGuard`, which sets tempClientId before the
 * role fetch and clears it after. Restores any prior value.
 */
export async function withPinnedClient<T>(clientId: string, fn: () => Promise<T>): Promise<T> {
  let prev: string | null = null;
  let pinned = false;
  if (clientId) {
    try {
      prev = localStorage.getItem(TEMP_CLIENT_ID_KEY);
      localStorage.setItem(TEMP_CLIENT_ID_KEY, clientId);
      pinned = true;
    } catch {
      /* storage unavailable — http() still tries the store/current-id fallbacks */
    }
  }
  try {
    return await fn();
  } finally {
    if (pinned) {
      try {
        if (prev === null) localStorage.removeItem(TEMP_CLIENT_ID_KEY);
        else localStorage.setItem(TEMP_CLIENT_ID_KEY, prev);
      } catch {
        /* storage unavailable */
      }
    }
  }
}

/**
 * Role-screen access data + check — the routing half of the legacy
 * `AuthorizeService.validateRoute` / `ValidateRouteGuard`
 * (`raptor-shell-ui/src/app/core/{services/authorize.service,guards/validate-route.guard}.ts`).
 *
 * The role-screen mapping (`GET .../RoleScreenMapping/GetRolesMapping?source=Portal`)
 * lists the app screens the signed-in user may open for the active client.
 * Legacy gates every `app-container/:app-id` navigation on it; the React shell
 * previously only used it to filter launcher tiles (`use-app-tiles.ts`), so any
 * appId that merely matched the GUID/slug shape could route (Bug #3 — e.g. MAD
 * loading for clients whose mapping only lists PIE).
 *
 * The mapping endpoint has no clientId parameter — it resolves against the RUM
 * token's active client — so results are cached per active clientId. A client
 * switch rotates the token and uses a different cache key, yielding a fresh
 * fetch for the new client.
 */

interface RoleScreenMappingDto {
  roleScreenModel?: { screenName: string }[];
}

/**
 * AppIds that bypass the role-screen check entirely. Port of legacy
 * `ALLOW_MFE_IDS` (`raptor-shell-ui/src/app/core/constants/allow-mfe-constants.ts`).
 */
const ALLOW_MFE_IDS: readonly string[] = ['notification-preferences'];

/**
 * Shell-chrome appIds that must never be role-gated: the landing MFE and the
 * app launcher. A mapping that omits them (or a transient empty mapping) must
 * not be able to lock the user out of the shell itself. This is a deliberate
 * safety net beyond legacy `ALLOW_MFE_IDS`.
 */
const SHELL_ESSENTIAL_APP_IDS: readonly string[] = ['home', 'workspace'];

const screensByClient = new Map<string, readonly string[]>();
const inFlight = new Map<string, Promise<readonly string[]>>();

/**
 * Session-scoped flag: has ANY client's role-screen mapping loaded successfully
 * at least once this browser session? Persisted in sessionStorage so it survives
 * a page REFRESH and a SignalR `clearRoleScreens()` (both wipe the in-memory
 * cache). This is what makes the first-login hard-block fire ONLY on a genuine
 * first login — after the first success the user "rides the session" and later
 * fetch failures degrade softly instead of being ejected to /login.
 */
const ROLE_MAPPING_BOOTSTRAPPED_KEY = 'ey.roleMapping.bootstrapped';

function markRoleMappingBootstrapped(): void {
  try {
    sessionStorage.setItem(ROLE_MAPPING_BOOTSTRAPPED_KEY, 'true');
  } catch {
    /* sessionStorage may be unavailable */
  }
}

/**
 * Whether the user has established a session (a role-screen mapping has loaded
 * successfully at least once this browser session). The route guard uses this to
 * decide whether a role-mapping fetch FAILURE is a first-login block (→ /login)
 * or an in-session transient (→ degrade softly). Legacy parity: legacy loads +
 * caches the mapping eagerly at frame-redirect and only blocks to LOGIN_URL when
 * that FIRST load fails.
 */
export function isRoleMappingBootstrapped(): boolean {
  try {
    return sessionStorage.getItem(ROLE_MAPPING_BOOTSTRAPPED_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Seed the cache from another fetch (e.g. the launcher's `use-app-tiles`). */
export function setRoleScreens(clientId: string, screens: readonly string[]): void {
  if (!clientId) return;
  screensByClient.set(clientId, screens);
  markRoleMappingBootstrapped();
}

/**
 * Whether a role-screen mapping is already cached for `clientId`. Used by the
 * route guard to decide, loop-safely, whether reverting a failed mid-session
 * client switch to the previous client is guaranteed not to re-fetch (and thus
 * cannot re-fail into a redirect loop).
 */
export function hasRoleScreens(clientId: string): boolean {
  return !!clientId && screensByClient.has(clientId);
}

/** Drop all cached mappings (e.g. on a role-mapping push update). */
export function clearRoleScreens(): void {
  screensByClient.clear();
  inFlight.clear();
}

/**
 * Resolve the user's allowed screen names for `clientId`, fetching once and
 * caching (deduped across concurrent callers). Rejects only when the underlying
 * request fails; callers decide how to degrade.
 */
export function ensureRoleScreens(clientId: string): Promise<readonly string[]> {
  const cached = screensByClient.get(clientId);
  if (cached) return Promise.resolve(cached);

  const existing = inFlight.get(clientId);
  if (existing) return existing;

  const request = withPinnedClient(clientId, () =>
    http<RoleScreenMappingDto>(Endpoints.roleScreenMapping()),
  )
    .then((res) => {
      const screens = (res.roleScreenModel ?? []).map((r) => r.screenName).filter(Boolean);
      screensByClient.set(clientId, screens);
      markRoleMappingBootstrapped();
      inFlight.delete(clientId);
      return screens;
    })
    .catch((err: unknown) => {
      inFlight.delete(clientId);
      throw err;
    });

  inFlight.set(clientId, request);
  return request;
}

/**
 * Whether `appId` is a shell surface that must NEVER be role-gated: the landing
 * MFE + app launcher (`SHELL_ESSENTIAL_APP_IDS`) or an explicitly allowlisted
 * MFE (`ALLOW_MFE_IDS`). Exported so the route guard can apply the SAME
 * short-circuit when it has to decide access WITHOUT a usable mapping (e.g. the
 * mapping fetch fails during a client-switch token rotation): without it a
 * "block on mapping error" rule would bounce `home` to itself in a loop.
 */
export function isEssentialOrAllowlistedScreen(appId: string): boolean {
  const id = appId.split('(')[0].toLowerCase();
  if (!id) return true;
  return ALLOW_MFE_IDS.includes(id) || SHELL_ESSENTIAL_APP_IDS.includes(id);
}

/**
 * Whether `appId` is permitted by the role-screen list. Faithful to legacy
 * `AuthorizeService.validateRoute`: the requested screen (here the appId) must
 * be a SUBSTRING of some mapping `screenName`
 * (`m.screenName.toLowerCase().indexOf(screenName) > -1`) — e.g. `pie` matches
 * `workspace/apps/pie`. The `(` popup suffix is stripped like legacy. The two
 * allowlists short-circuit the check.
 */
export function isScreenAllowed(appId: string, screens: readonly string[]): boolean {
  const id = appId.split('(')[0].toLowerCase();
  if (!id) return true;
  if (isEssentialOrAllowlistedScreen(id)) return true;
  return screens.some((screen) => screen.toLowerCase().includes(id));
}

/**
 * Side-nav VISIBILITY check — the port of legacy
 * `AuthorizeService.validateRouteForSideNav`, deliberately STRICTER than
 * {@link isScreenAllowed}/`validateRoute`: the appId must match a whole path
 * SEGMENT of a screen name, not a substring.
 *
 * Legacy switched the side nav to this exact-segment rule because the substring
 * check surfaced the "Admin" item for users whose only match was
 * `workspace/apps/WORKITEMADMINUI` — the `admin` substring inside `…ADMINUI`.
 * Real admin screens are `admin/apps/…`, so requiring `admin` to be a full
 * segment keeps the Admin rail item hidden for non-admins (Bug #5) while still
 * showing every app the user can open (screens are `workspace/apps/{appId}`, so
 * `{appId}` is always a distinct segment). The two allowlists short-circuit.
 */
export function isSideNavItemAllowed(appId: string, screens: readonly string[]): boolean {
  const id = appId.split('(')[0].toLowerCase();
  if (!id) return true;
  if (isEssentialOrAllowlistedScreen(id)) return true;
  return screens.some((screen) => screen.toLowerCase().split('/').filter(Boolean).includes(id));
}
